import { useEffect, useRef, useState, type ReactNode, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { DragHandle } from '@/components/ui/DragHandle';
import { getWidgetDef, type WidgetDef } from './registry';
import { ROW_H, GAP, GRID_COLS, DOT_SUBDIV } from './resize';
import { GRID_DOT, CARD_TITLE } from './homeStyles';
import { resolveContainerBoxes } from './placeContainer';
import { moveElement, resolveResizeBox, bottomRow, type Box } from './place';
import { WidgetCard } from './WidgetCard';
import type { WidgetInstance } from './types';

// Customize-mode canvas with FREE placement: each widget renders at its explicit
// (x,y) grid cell; dragging drops the widget where released and PUSHES overlapping
// neighbors aside (place.ts). Live preview is DOM-only (direct style writes on the
// cell wrappers, no per-move React re-render) so Recharts widgets never reflow
// mid-drag; a single commit fires on drop. The grabbed widget is shown as a
// tile-sized ghost following the cursor.
type DragState = {
  from: string; label: string; icon: ReactNode; accent: string;
  w: number; h: number; grabX: number; grabY: number; x0: number; y0: number;
};

const cellStyle = (b: Box): [string, string] => [`${b.x + 1} / span ${b.w}`, `${b.y + 1} / span ${b.h}`];

// Widget resize is grabbable from every corner. A corner keeps the OPPOSITE
// corner fixed, so dragging it changes the tile's origin (x/y) as well as size.
type Corner = 'nw' | 'ne' | 'sw' | 'se';
type Bounds = { minW: number; maxW: number; minH: number; maxH: number };
const MAX_H = 16; // matches resize.MAX_WIDGET_H (server clamps the same)
// Knobs straddle each corner (out into the gap) so they clear the tile's own
// content/scrollbar and read as obvious grab points.
const CORNER_POS: Record<Corner, string> = {
  nw: '-top-1 -left-1', ne: '-top-1 -right-1', sw: '-bottom-1 -left-1', se: '-bottom-1 -right-1',
};
const CORNER_CURSOR: Record<Corner, string> = {
  nw: 'cursor-nwse-resize', ne: 'cursor-nesw-resize', sw: 'cursor-nesw-resize', se: 'cursor-nwse-resize',
};

// Desired box for a corner drag (cells), keeping the opposite edges fixed and
// clamping to the widget's bounds and the grid extents.
function cornerBox(base: Box, corner: Corner, dcx: number, dcy: number, bounds: Bounds | undefined, cols: number): { x: number; y: number; w: number; h: number } {
  const right = base.x + base.w, bottom = base.y + base.h;
  const movesLeft = corner === 'nw' || corner === 'sw';
  const movesTop = corner === 'nw' || corner === 'ne';
  let w = movesLeft ? base.w - dcx : base.w + dcx;
  let h = movesTop ? base.h - dcy : base.h + dcy;
  const maxW = bounds ? bounds.maxW : cols;
  const maxH = bounds ? bounds.maxH : MAX_H;
  // Width is bounded by the fixed grid (12 cols); height is NOT — the canvas
  // grows/scrolls, so growing DOWN is capped only by the widget's max height
  // (not MAX_H - y, which wrongly froze tiles that sit below the top row).
  const hiW = movesLeft ? Math.min(maxW, right) : Math.min(maxW, cols - base.x);
  const hiH = movesTop ? Math.min(maxH, bottom) : maxH;
  w = Math.max(bounds ? bounds.minW : 1, Math.min(hiW, w));
  h = Math.max(bounds ? bounds.minH : 1, Math.min(hiH, h));
  return { x: movesLeft ? right - w : base.x, y: movesTop ? bottom - h : base.y, w, h };
}

export function WidgetGridEdit({ widgets, onMove, onResizeBox, onSetSize, onRemove, onSetConfig, extraMenuItems, emptyHint }: {
  widgets: WidgetInstance[];
  onMove: (id: string, x: number, y: number) => void;
  onResizeBox: (id: string, x: number, y: number, w: number, h: number) => void;
  onSetSize: (id: string, w: number) => void;
  onRemove: (id: string) => void;
  onSetConfig: (id: string, config: Record<string, unknown>) => void;
  extraMenuItems?: (w: WidgetInstance) => { label: string; onClick: () => void }[];
  emptyHint?: string;
}) {
  const gridRef = useRef<HTMLDivElement>(null);
  const cellRefs = useRef<Map<string, HTMLElement>>(new Map());
  const ghostRef = useRef<HTMLDivElement>(null);
  const [configOpen, setConfigOpen] = useState<string | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  // Compact widgets (KPI/Picture) host their config in a floating panel; clicking
  // anywhere outside that panel dismisses it. Only compact configs attach this
  // ref, so inline (non-compact) config — which lives inside the card — is left
  // alone (panel is null → no auto-close).
  const configPanelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!configOpen) return;
    const onDown = (e: Event) => {
      const panel = configPanelRef.current;
      if (!panel) return;
      if (!panel.contains(e.target as Node)) setConfigOpen(null);
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [configOpen]);

  const boxes = resolveContainerBoxes(widgets);
  const byId = new Map(boxes.map((b) => [b.id, b]));
  const rows = Math.max(bottomRow(boxes), 3);

  // Write a preview layout straight to the DOM (no React state / no widget re-render).
  const applyPreview = (preview: Box[]) => {
    for (const b of preview) {
      const el = cellRefs.current.get(b.id);
      if (el) { const [c, r] = cellStyle(b); el.style.gridColumn = c; el.style.gridRow = r; }
    }
  };
  // Restore the committed layout to the DOM (used on cancel).
  const revertStyles = () => applyPreview(boxes);

  // Grid-geometry snapshot for a drag/resize session (geometry is stable during it).
  const gridGeom = () => {
    const grid = gridRef.current;
    if (!grid) return null;
    const gr = grid.getBoundingClientRect();
    const colW = (grid.clientWidth - (GRID_COLS - 1) * GAP) / GRID_COLS;
    return { left: gr.left, top: gr.top, colPitch: colW + GAP, rowPitch: ROW_H + GAP, colW };
  };

  const startDrag = (e: ReactPointerEvent, w: WidgetInstance, def: WidgetDef) => {
    if (e.button && e.button !== 0) return;
    e.preventDefault(); e.stopPropagation();
    const handleEl = e.currentTarget as HTMLElement;
    const pid = e.pointerId;
    try { handleEl.setPointerCapture?.(pid); } catch { /* best-effort */ }
    const geom = gridGeom(); if (!geom) return;
    const box = byId.get(w.id) as Box;
    const cellRect = cellRefs.current.get(w.id)?.getBoundingClientRect();
    const grabX = cellRect ? e.clientX - cellRect.left : 24;
    const ghostH = Math.min(cellRect?.height ?? 120, 320);
    const grabY = Math.min(cellRect ? e.clientY - cellRect.top : 18, ghostH - 8);
    const snapshot = boxes;
    let targetX = box.x, targetY = box.y, lastX = NaN, lastY = NaN, changed = false;
    setDrag({ from: w.id, label: def.label, icon: def.icon, accent: def.accent, w: cellRect?.width ?? 240, h: ghostH, grabX, grabY, x0: e.clientX, y0: e.clientY });
    document.body.style.userSelect = 'none';
    const onPtrMove = (ev: PointerEvent) => {
      const g = ghostRef.current;
      if (g) { g.style.left = `${ev.clientX - grabX}px`; g.style.top = `${ev.clientY - grabY}px`; }
      const tx = Math.round((ev.clientX - grabX - geom.left) / geom.colPitch);
      const ty = Math.round((ev.clientY - grabY - geom.top) / geom.rowPitch);
      if (tx === lastX && ty === lastY) return;
      lastX = tx; lastY = ty; changed = true;
      const preview = moveElement(snapshot, w.id, tx, ty, GRID_COLS);
      const mv = preview.find((b) => b.id === w.id) as Box;
      targetX = mv.x; targetY = mv.y;
      applyPreview(preview);
    };
    const end = (commit: boolean) => {
      window.removeEventListener('pointermove', onPtrMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      try { handleEl.releasePointerCapture?.(pid); } catch { /* already released */ }
      document.body.style.userSelect = '';
      // Skip the commit (and its re-render) on a no-op: cancel, or a click / drag
      // that didn't change the tile's cell.
      if (commit && changed && (targetX !== box.x || targetY !== box.y)) onMove(w.id, targetX, targetY);
      else revertStyles();
      setDrag(null);
    };
    const onUp = () => end(true);
    const onCancel = () => end(false);
    window.addEventListener('pointermove', onPtrMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
  };

  const startResize = (e: ReactPointerEvent, w: WidgetInstance, corner: Corner) => {
    if (e.button && e.button !== 0) return;
    e.preventDefault(); e.stopPropagation();
    const geom = gridGeom(); if (!geom) return;
    const handleEl = e.currentTarget as HTMLElement;
    const pid = e.pointerId;
    try { handleEl.setPointerCapture?.(pid); } catch { /* best-effort */ }
    const base = byId.get(w.id) as Box;
    const snapshot = boxes;
    const start = { x: e.clientX, y: e.clientY };
    const bounds = getWidgetDef(w.type)?.bounds;
    let target = { x: base.x, y: base.y, w: base.w, h: base.h };
    let lastKey = '';
    const onPtrMove = (ev: PointerEvent) => {
      const dcx = geom.colW > 0 ? Math.round((ev.clientX - start.x) / geom.colW) : 0;
      const dcy = Math.round((ev.clientY - start.y) / ROW_H);
      const desired = cornerBox(base, corner, dcx, dcy, bounds, GRID_COLS);
      const key = `${desired.x},${desired.y},${desired.w},${desired.h}`;
      if (key === lastKey) return;
      lastKey = key;
      const preview = resolveResizeBox(snapshot, w.id, desired, GRID_COLS);
      const mv = preview.find((b) => b.id === w.id) as Box;
      target = { x: mv.x, y: mv.y, w: mv.w, h: mv.h };
      applyPreview(preview);
    };
    const end = (commit: boolean) => {
      window.removeEventListener('pointermove', onPtrMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      try { handleEl.releasePointerCapture?.(pid); } catch { /* already released */ }
      const moved = target.x !== base.x || target.y !== base.y || target.w !== base.w || target.h !== base.h;
      if (commit && moved) onResizeBox(w.id, target.x, target.y, target.w, target.h);
      else revertStyles(); // cancel or no-op resize: restore the committed layout
    };
    const onUp = () => end(true);
    const onCancel = () => end(false);
    window.addEventListener('pointermove', onPtrMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
  };

  const gridStyle: CSSProperties = {
    gridTemplateColumns: `repeat(${GRID_COLS},minmax(0,1fr))`,
    gridTemplateRows: `repeat(${rows}, ${ROW_H}px)`,
    // Implicit rows (a live drag/resize can push a tile below `rows`, which only
    // updates on commit) keep the fixed row height so the preview doesn't collapse.
    gridAutoRows: `${ROW_H}px`,
    gap: GAP,
    // Dotted alignment grid, subdivided DOT_SUBDIV x per cell. Offset by half a
    // tile so the dots land ON the cell-edge lattice (where widget edges snap),
    // not inside cells under the tiles.
    backgroundImage: `radial-gradient(circle, ${GRID_DOT} 1.5px, transparent 2px)`,
    backgroundSize: `calc((100% + ${GAP}px) / ${GRID_COLS * DOT_SUBDIV}) ${(ROW_H + GAP) / DOT_SUBDIV}px`,
    backgroundPosition: `calc((100% + ${GAP}px) / ${GRID_COLS * DOT_SUBDIV * 2}) ${(ROW_H + GAP) / (DOT_SUBDIV * 2)}px`,
    minHeight: `${ROW_H * 3 + GAP * 2}px`,
  };

  return (
    <div ref={gridRef} className="grid" style={gridStyle}>
      {widgets.map((w) => {
        const def = getWidgetDef(w.type); if (!def) return null;
        const box = byId.get(w.id) as Box;
        const [gc, gr] = cellStyle(box);
        const isCompact = !!def.compact;
        const Render = def.Render; const Config = def.ConfigForm;
        const isDragging = drag?.from === w.id;
        return (
          <div key={w.id}
            ref={(el) => { if (el) cellRefs.current.set(w.id, el); else cellRefs.current.delete(w.id); }}
            style={{ gridColumn: gc, gridRow: gr, position: 'relative' }}>
            <WidgetCard def={def} title={def.title?.(w.config)} headerAction={def.headerAction?.(w.config)} className={`h-full transition-opacity ${isDragging ? 'opacity-30' : ''}`} compact={isCompact}
              onHeaderPointerDown={(e) => startDrag(e, w, def)}
              headerRight={<span title="Drag to move" className="text-muted select-none px-0.5 [&_svg]:w-4 [&_svg]:h-4"><DragHandle size={16} /></span>}
              menuItems={[
                { label: configOpen === w.id ? 'Hide config' : 'Configure', onClick: () => setConfigOpen((c) => c === w.id ? null : w.id) },
                ...(isCompact ? [] : [
                  { label: 'Small', onClick: () => onSetSize(w.id, 4) },
                  { label: 'Medium', onClick: () => onSetSize(w.id, 8) },
                  { label: 'Full width', onClick: () => onSetSize(w.id, 12) },
                ]),
                ...(extraMenuItems ? extraMenuItems(w) : []),
                { label: 'Remove', onClick: () => onRemove(w.id) },
              ]}>
              {/* Compact tiles (KPI/Picture) are too small to host their config
                  inline, so it opens in a floating panel below the tile instead
                  (see after the card). They keep showing their preview here. */}
              {configOpen === w.id && !isCompact
                ? <Config config={w.config} onChange={(c) => onSetConfig(w.id, c)} />
                : <Render config={w.config} w={box.w} h={box.h} />}
            </WidgetCard>
            {configOpen === w.id && isCompact && (
              <div ref={configPanelRef} className="absolute left-0 top-full mt-1 z-50 w-64 max-w-[85vw] rounded-lg border border-border bg-surface shadow-lg p-2">
                <Config config={w.config} onChange={(c) => onSetConfig(w.id, c)} />
              </div>
            )}
            {isDragging && (
              <div className="absolute inset-0 rounded-[12px] border-2 border-dashed border-primary/60 bg-primary/[0.05] pointer-events-none" />
            )}
            {(['nw', 'ne', 'sw', 'se'] as const).map((corner) => (
              // Small 4px dot (visible grab sign) inside a larger transparent hit
              // area so it's easy to grab without looking like a bubble.
              <span key={corner} onPointerDown={(e) => startResize(e, w, corner)} title="Drag to resize"
                className={`absolute z-20 w-4 h-4 flex items-center justify-center touch-none select-none ${CORNER_POS[corner]} ${CORNER_CURSOR[corner]}`}>
                <span className="w-1 h-1 rounded-full bg-primary" />
              </span>
            ))}
          </div>
        );
      })}
      {widgets.length === 0 && emptyHint && (
        <div style={{ gridColumn: 'span 12 / span 12' }} className="rounded-[12px] border border-dashed border-border p-6 text-center text-sm text-muted">{emptyHint}</div>
      )}
      {drag && createPortal(
        <div ref={ghostRef} style={{
            position: 'fixed', left: drag.x0 - drag.grabX, top: drag.y0 - drag.grabY,
            width: drag.w, height: drag.h, zIndex: 60, pointerEvents: 'none', opacity: 0.95,
          }}
          className="flex flex-col rounded-[12px] border border-[#b8c4d6] bg-surface shadow-xl overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border-light shrink-0">
            <span className="w-5 h-5 rounded-md inline-flex items-center justify-center text-white shrink-0 [&_svg]:w-3 [&_svg]:h-3" style={{ background: drag.accent }}>{drag.icon}</span>
            <span className={`${CARD_TITLE} truncate flex-1`}>{drag.label}</span>
            <DragHandle size={16} className="text-muted" />
          </div>
          <div className="flex-1 min-h-0 p-3 space-y-2 overflow-hidden">
            <div className="h-2.5 rounded bg-border-light w-3/4" />
            <div className="h-2.5 rounded bg-border-light w-1/2" />
            <div className="h-2.5 rounded bg-border-light w-2/3" />
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
